import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

// First-touch referrer / landing page / query params, set by the snippet in public/index.html
// and the marketing site layouts on .sharpemetrix.com.
const getSignupSource = () => {
  try {
    const raw = document.cookie.split('; ').find((c) => c.startsWith('sm_first_touch='));
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw.slice('sm_first_touch='.length)));
  } catch (_) {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRecord, setUserRecord] = useState(null); // single row from user_permissions
  const [loading, setLoading] = useState(true);
  const [recordLoading, setRecordLoading] = useState(false);
  const authStateHandled = React.useRef(false); // prevent double-load during init

  const loadUserRecord = useCallback(async (userId) => {
    if (!userId) return null;
    setRecordLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_permissions')
        .select('*')
        .eq('user_id', userId)
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[Auth] loadUserRecord error:', error.message);
        return null;
      }
      const record = data ?? null;
      // Supabase may return the JSONB `data` column as a string scalar — parse it.
      if (record && typeof record.data === 'string') {
        try { record.data = JSON.parse(record.data); } catch (_) {}
      }
      setUserRecord(record);
      return record;
    } catch (err) {
      console.error('[Auth] loadUserRecord exception:', err);
      return null;
    } finally {
      setRecordLoading(false);
    }
  }, []);

  // Auto-expire trial via edge function on page load
  const checkTrialExpiry = async (userId) => {
    try {
      await supabase.functions.invoke('subscription', {
        body: { action: 'expire_trial' },
      });
    } catch (_) {
      // Non-critical — don't block the app
    }
  };

  // Hard safety net: spinner never stays on more than 4 seconds
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        if (session?.access_token) {
          localStorage.setItem('access_token', session.access_token);
        } else {
          localStorage.removeItem('access_token');
        }
        if (currentUser && !authStateHandled.current) {
          await loadUserRecord(currentUser.id);
        }
      } catch (err) {
        console.error('[Auth] initialize error:', err);
      } finally {
        setLoading(false);
      }
    };

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (session?.access_token) {
        localStorage.setItem('access_token', session.access_token);
      } else {
        localStorage.removeItem('access_token');
        setUserRecord(null);
      }
      if (currentUser && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        authStateHandled.current = true;
        // Defer out of the onAuthStateChange callback — Supabase must finish
        // committing the session internally before RLS-gated queries will work.
        setTimeout(async () => {
          await loadUserRecord(currentUser.id);
          checkTrialExpiry(currentUser.id);
          setLoading(false);
        }, 0);
      }
    });

    return () => subscription?.unsubscribe();
  }, [loadUserRecord]);

  // ── Passwordless auth ──────────────────────────────────────────────────────

  const notifyRegistration = (email, { loggedIn = false, resent = false } = {}) => {
    const slack_message = loggedIn
      ? `New signup — logged in — ${email} (entered code)`
      : resent
      ? `New signup — not logged in yet — ${email} (resent code, watch if they need help)`
      : `New signup — not logged in yet — ${email} (sent code, watch if they need help)`;

    fetch('https://hooks.zapier.com/hooks/catch/8830689/uytx9f3/', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: loggedIn
          ? 'New SharpeMetrix Registration'
          : 'New SharpeMetrix Signup (not logged in)',
        email,
        logged_in:           loggedIn,
        signup_step:         loggedIn ? 'logged_in' : (resent ? 'code_resent' : 'code_sent'),
        slack_message,
        message:             slack_message,
        subscription_tier:   'none',
        subscription_status: 'inactive',
        signup_source:       getSignupSource(),
      }),
    }).catch((err) => console.error('Zapier webhook error:', err));
  };

  const sendOtp = async (email, { notifySignup = false, resent = false } = {}) => {
    const signupSource = getSignupSource();
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        // Supabase only applies `data` when it creates a new user, so logins don't overwrite it.
        ...(signupSource && { data: { signup_source: signupSource } }),
      },
    });
    if (error) throw error;
    if (notifySignup) notifyRegistration(email, { loggedIn: false, resent });
  };

  const verifyOtp = async (email, token) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    if (error) throw error;

    if (data.session) {
      localStorage.setItem('access_token', data.session.access_token);
      setUser(data.session.user);
      const record = await loadUserRecord(data.session.user.id);
      const isNewUser = !record; // no user_permissions row = brand new signup

      if (isNewUser) {
        notifyRegistration(data.session.user.email, { loggedIn: true });
      }

      return {
        user: data.session.user,
        userRecord: record,
        needsOnboarding: !record?.onboarding_completed,
      };
    }
    return { needsOnboarding: true };
  };

  const logout = async () => {
    setUser(null);
    setUserRecord(null);
    localStorage.removeItem('access_token');
    await supabase.auth.signOut().catch(() => {});
  };

  // ── All writes go through the subscription edge function ─────────────────
  // Users cannot touch approved/admin directly — business logic lives server-side.

  const callSubscriptionFn = async (action, payload = {}) => {
    const { data, error } = await supabase.functions.invoke('subscription', {
      body: { action, ...payload },
    });
    if (error) throw error;
    // Refresh local record after any write
    await loadUserRecord(user.id);
    return data;
  };

  const activateTrial = async (profileData = {}) => {
    const result = await callSubscriptionFn('activate_trial', { data: profileData });

    const isDiscoveryCall = profileData.discovery_call === true;
    const event = isDiscoveryCall ? 'Discovery Call Booked' : 'Trial Activated';
    const slack_message = isDiscoveryCall
      ? `Discovery call booked after signup — ${user?.email || 'unknown'}${profileData.aum_range ? ` · AUM ${profileData.aum_range}` : ''}`
      : `30-day trial started — ${user?.email || 'unknown'}${profileData.role ? ` · ${profileData.role}` : ''}${profileData.aum_range ? ` · ${profileData.aum_range}` : ''}`;

    // Fire Zapier → HubSpot / Slack
    fetch('https://hooks.zapier.com/hooks/catch/8830689/uytx9f3/', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        slack_message,
        message:             slack_message,
        email:               user?.email,
        subscription_tier:   'trial',
        subscription_status: 'active',
        first_name:          profileData.first_name,
        last_name:           profileData.last_name,
        firm_name:           profileData.firm_name,
        phone:               profileData.phone,
        role:                profileData.role,
        aum_range:           profileData.aum_range,
        aum_estimate:        profileData.aum_estimate,
        referral_source:     profileData.referral_source,
        signup_source:       user?.user_metadata?.signup_source ?? getSignupSource(),
        discovery_call:      isDiscoveryCall,
      }),
    }).catch((err) => console.error('Zapier webhook error:', err));

    return result;
  };

  const selectPaidPlan = async (tier, profileData = {}) => {
    return callSubscriptionFn('select_paid_plan', { tier, data: profileData });
  };

  const completeOnboarding = async () => {
    return callSubscriptionFn('complete_onboarding');
  };

  const updateProfileData = async (fields) => {
    return callSubscriptionFn('update_profile', { data: fields });
  };

  const createPortalSession = async () => {
    const result = await callSubscriptionFn('create_portal_session');
    const portalUrl = result?.data?.portal_url;
    if (portalUrl) window.location.href = portalUrl;
  };

  // Convenience flag
  const isApproved = userRecord?.approved === true;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-100">
        <div className="flex flex-col items-center gap-4">
          <img src="/logo_wordmark.svg" alt="SharpeMetrix" className="h-8 opacity-60" />
          <div className="w-6 h-6 border-2 border-primary-100 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user,
      userRecord,
      isApproved,
      loading,
      recordLoading,
      sendOtp,
      verifyOtp,
      logout,
      activateTrial,
      selectPaidPlan,
      completeOnboarding,
      updateProfileData,
      createPortalSession,
      loadUserRecord,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
